
import {defineComponent} from "vue";
import {Exception} from "@/api/interfaces";
import {DpException} from "@/exception/DpException";
import CatalogBuildStatus from "@/components/layouts/catalog/CatalogBuildStatus.vue";
import CustomerProductProduct from "@/components/layouts/catalog/customer-products/CustomerProductProduct.vue";
import CustomerProductProperties from "@/components/layouts/catalog/customer-products/CustomerProductProperties.vue";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {
  CustomerProductWithImageDetailRestDtoModel
} from "@/models/api/pharma-cpc-product-mgmt/CustomerProductWithImageDetailRestDtoModel";
import {FormMixin} from "@/components/base/FormMixin";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {CustomerProductsRestService} from "@/services/rest/cpc-product-mgmt/CustomerProductsRestService";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import BaseOffcanvas from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvas.vue";
import ProductDetailPhotosTab from "@/components/layouts/catalog/product-detail/ProductDetailPhotosTab.vue";
import {PhotoRestService} from "@/services/rest/photo/PhotoRestService";
import {I18nContext} from "@/context/I18nContext";
import {Tooltip} from "bootstrap";

export default defineComponent({
  name: "CustomerProductsDetail",
  mixins: [FormMixin],
  components: {
    ProductDetailPhotosTab,
    BaseOffcanvas,
    GenericFormWrapper,
    AlertError2,
    BaseTitle,
    CatalogBuildStatus,
    Product: CustomerProductProduct,
    Properties: CustomerProductProperties,
  },
  props: ['mode'],
  data() {
    return {
      currentComponent: 'product' as string,
      exceptions: [] as Exception[],
      productCode: this.$route.params.id as string,
      product: CustomerProductWithImageDetailRestDtoModel.createWithDefaults(),
      createdProductCode: null as string | null,
    }
  },
  mounted() {
    this.initialize();
  },
  computed: {
    currentProperties(): Object {
      switch (this.currentComponent) {
        case 'product':
          return {
            mode: this.mode,
          };
        case 'properties':
          return {};
        default:
          throw [new DpException('Unsupported customer product component')];
      }
    },
  },
  methods: {
    async doInitialize(): Promise<void> {
      // Initialize tooltips after DOM is ready
      await this.$nextTick(() => {
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.map((tooltipTriggerEl) => new Tooltip(tooltipTriggerEl));
      });
    },
    async doReload() {
      if (this.mode === PageDetailModeEnum.NEW) {
        this.product = CustomerProductWithImageDetailRestDtoModel.createWithDefaults();
      } else {
        this.product = await CustomerProductsRestService.getInstance().getCustomerProduct(this.productCode, I18nContext.getDefaultLanguage());
      }
    },
    setCurrentComponent(cmp: string) {
      this.currentComponent = cmp;
    },
    async doSubmit() {
      if (this.mode === PageDetailModeEnum.NEW) {
        this.createdProductCode = await CustomerProductsRestService.getInstance().createCustomerProduct(this.product.product);
      } else {
        await CustomerProductsRestService.getInstance().updateCustomerProduct(this.productCode, this.product.product);
      }
    },
    async afterSave() {
      if (this.mode === PageDetailModeEnum.NEW) {
        await this.$router.push({name: 'edit-customer-product', params: {id: this.createdProductCode!}});
      } else {
        await handleSavedSuccessfully();
        await this.reloadContent();
      }
    },
    openPhotosOffCanvas(): void {
      this.openOffCanvas('offcanvas-customer-product-photos');
    },
    async reloadImage(): Promise<void> {
      PhotoRestService.getInstance().clearProductImageCache(this.product.product.cnk_code, this.productCode, I18nContext.getDefaultLanguage());
      const newImageUrl: string = await PhotoRestService.getInstance().getProductImageUrl(this.product.product.cnk_code, null, I18nContext.getDefaultLanguage());
      this.product = {
        ...this.product,
        main_image_url: newImageUrl
      };
    }
  },
});
